import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { childTicketService, type ChildTicketTask } from '@/lib/services/childTicketService'
import { TicketService } from '@/lib/services/ticket'
import type { FESchedule } from '@/types/parentTicket'
import type { FieldEngineerRecommendation } from '@/types/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type Props = {
  isOpen: boolean
  onClose: () => void
  parentTicketId: string
  task: ChildTicketTask
  sourceSchedule: FESchedule | null
  onReassigned: (result: { new_task: ChildTicketTask }) => void
}

function formatDistance(km: number | null | undefined): string {
  if (km == null || !Number.isFinite(km)) return '—'
  return `${Math.round(Number(km))} km`
}

function getFeUserId(rec: FieldEngineerRecommendation): number | null {
  const id = rec.user_id ?? rec.field_engineer_id
  return id != null && Number(id) > 0 ? Number(id) : null
}

function scheduleToRecommendIso(schedule: FESchedule): string | null {
  const date = (schedule.schedule_date ?? '').trim()
  if (!date) return null
  const time = (schedule.schedule_time ?? '09:00').trim().slice(0, 5) || '09:00'
  const iso = new Date(`${date}T${time}:00`)
  if (Number.isNaN(iso.getTime())) return `${date}T${time}:00`
  return iso.toISOString()
}

export default function ReassignEngineerModal({
  isOpen,
  onClose,
  parentTicketId,
  task,
  sourceSchedule,
  onReassigned,
}: Props) {
  const { showSuccess, showError } = useToastNotification()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [recommendations, setRecommendations] = useState<FieldEngineerRecommendation[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const currentFeUserId = sourceSchedule?.fe_user_id ?? task.assigned_to ?? null

  const loadRecommendations = useCallback(async () => {
    if (!sourceSchedule?.schedule_date) {
      setRecommendations([])
      setLoadError(null)
      return
    }

    setLoading(true)
    setLoadError(null)
    setRecommendations([])

    try {
      const scheduleIso = scheduleToRecommendIso(sourceSchedule)
      const res = await TicketService.recommendEngineersForTicket(parentTicketId, {
        schedule: scheduleIso,
        limit: 8,
      })

      if (!res.success) {
        throw new Error(res.message || 'Failed to load engineer recommendations')
      }

      const filtered = (res.data ?? []).filter((rec) => {
        const feUserId = getFeUserId(rec)
        return feUserId != null && feUserId !== Number(currentFeUserId)
      })

      setRecommendations(filtered)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load recommendations')
    } finally {
      setLoading(false)
    }
  }, [currentFeUserId, parentTicketId, sourceSchedule])

  useEffect(() => {
    if (!isOpen) {
      setReason('')
      return
    }
    void loadRecommendations()
  }, [isOpen, loadRecommendations])

  const handleSelect = async (rec: FieldEngineerRecommendation) => {
    const feUserId = getFeUserId(rec)
    if (!feUserId) {
      showError(new Error('Selected engineer is missing a user id'))
      return
    }

    if (!sourceSchedule?.schedule_date) {
      showError(new Error('No linked FE schedule found for this visit'))
      return
    }

    setSubmitting(true)
    try {
      const result = await childTicketService.reassignEngineer(parentTicketId, task.id, {
        fe_user_id: feUserId,
        reason: reason.trim() !== '' ? reason.trim() : undefined,
      })

      const newTaskNumber = result.new_task.task_number ?? `#${result.new_task.id}`
      showSuccess(`Reassigned to ${rec.full_name || 'field engineer'} — replacement visit ${newTaskNumber}`)
      onReassigned({ new_task: result.new_task })
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setSubmitting(false)
    }
  }

  const selectableRecommendations = useMemo(
    () =>
      recommendations.filter((rec) => {
        const feUserId = getFeUserId(rec)
        return feUserId != null && feUserId !== Number(currentFeUserId)
      }),
    [currentFeUserId, recommendations],
  )

  return (
    <Modal isOpen={isOpen} onClose={submitting ? () => undefined : onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Reassign engineer</ModalHeader>
        <ModalCloseButton isDisabled={submitting} />
        <ModalBody pb={2}>
          <Text fontSize="sm" color="gray.600" mb={4}>
            The current visit will be marked as superseded and retained in history. A replacement visit
            will be created for the selected engineer.
          </Text>

          <FormControl mb={4}>
            <FormLabel fontSize="sm">Reassignment reason (optional)</FormLabel>
            <Textarea
              size="sm"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Why is this visit being reassigned?"
              isDisabled={submitting}
              rows={2}
            />
          </FormControl>

          {loading ? (
            <HStack py={6} justify="center">
              <Spinner size="sm" />
              <Text fontSize="sm" color="gray.500">
                Loading recommendations…
              </Text>
            </HStack>
          ) : null}

          {!loading && loadError ? (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="red.500">
                {loadError}
              </Text>
              <Button size="sm" variant="outline" onClick={() => void loadRecommendations()} isDisabled={submitting}>
                Retry
              </Button>
            </VStack>
          ) : null}

          {!loading && !loadError && selectableRecommendations.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No alternative engineer recommendations are available for this visit.
            </Text>
          ) : null}

          {!loading && !loadError && selectableRecommendations.length > 0 ? (
            <VStack align="stretch" spacing={2}>
              {selectableRecommendations.map((rec) => {
                const feUserId = getFeUserId(rec)
                const metaParts = [
                  rec.distance_km != null ? formatDistance(rec.distance_km) : null,
                  typeof rec.score === 'number' ? `score ${Math.round(rec.score)}` : null,
                  rec.schedule_label || null,
                  rec.cluster || rec.launch_point || null,
                ].filter(Boolean)

                return (
                  <Box
                    key={`${feUserId ?? rec.field_engineer_id}-${rec.full_name}`}
                    borderWidth="1px"
                    borderRadius="md"
                    px={3}
                    py={2.5}
                  >
                    <HStack justify="space-between" align="center" spacing={3}>
                      <Box minW={0}>
                        <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                          {rec.full_name || `Engineer #${feUserId ?? '—'}`}
                        </Text>
                        {metaParts.length > 0 ? (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {metaParts.join(' · ')}
                          </Text>
                        ) : null}
                      </Box>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        flexShrink={0}
                        isDisabled={feUserId == null || submitting}
                        isLoading={submitting}
                        onClick={() => void handleSelect(rec)}
                      >
                        Select
                      </Button>
                    </HStack>
                  </Box>
                )
              })}
            </VStack>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
